import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { DataGridPremium, type GridColDef, type GridRenderCellParams, type GridRowParams } from '@mui/x-data-grid-premium';
import ActivityCell, { type ActivityEntry } from '../../../components/ActivityCell';
import type { TrackingStats } from '../../../services/tracking.service';
import type { DashboardConfig, DashboardPanel } from '../types';
import { resolveDatePreset } from '../../../utils/date-presets';
import { useReportDefaults } from '../../../hooks/useReportDefaults';
import { labelForField, levelDims } from '../../tracking/tracking-page.format';
import {
  metricLabel, formatMetric, orderRowsByGroup, metricSign, dimValue, dimName,
  ENTITY_ACTIVITY_GROUPINGS, activityKind, ENTITY_FILTER_FIELDS, TRACKING_FILTERABLE,
} from '../format';

const fmtDay = (d: Date | null): string => (d ? format(d, 'yyyy-MM-dd') : '');

export default function TablePanel({ panel, stats, dashboard, editMode }: {
  panel: DashboardPanel; stats: TrackingStats; dashboard: DashboardConfig; editMode?: boolean;
}) {
  const navigate = useNavigate();
  const defaultTz = useReportDefaults().timezone;
  const groupBy = panel.query.group_by;
  const dims = levelDims(groupBy);
  const showActivity = dims.length === 1 && ENTITY_ACTIVITY_GROUPINGS.has(groupBy);
  // A row can deep-link into Tracking when its grouping is a date or a filterable field —
  // but not while arranging panels in edit mode.
  const linkable = !editMode && dims.every((d) => d === 'date' || TRACKING_FILTERABLE.has(d));

  const openInTracking = (row: Record<string, unknown>) => {
    const tz = panel.query.timezone ?? dashboard.timezone ?? defaultTz;
    const [rs, re] = resolveDatePreset(panel.query.time_preset ?? dashboard.time_preset);
    const p = new URLSearchParams();
    p.set('tz', String(tz));
    // Carry the panel's own bot/attribution choices so Tracking's totals match the panel.
    p.set('nobots', (panel.query.exclude_bots ?? true) ? '1' : '0');
    p.set('convclick', panel.query.conv_time_basis === 'click' ? '1' : '0');
    const day = dims.includes('date') ? dimValue(row, 'date') : null;
    p.set('dates', day ? `${day}..${day}` : `${fmtDay(rs)}..${fmtDay(re)}`);
    const fields = dims.filter((d) => d !== 'date');
    // Replace, not append: appended filters OR (IN [...]) and widen past the row.
    const filters = [
      ...(panel.query.filters ?? []).filter((f) => !fields.includes(f.field)),
      ...fields.map((d) => ({ field: d, value: dimValue(row, d), label: dimName(row, d) })),
    ];
    if (filters.length) p.set('filters', JSON.stringify(filters));
    if (fields.length === 1 && ENTITY_FILTER_FIELDS.includes(fields[0])) p.set('ffield', fields[0]);
    navigate(`/tracking?${p.toString()}`);
  };

  const columns: GridColDef[] = [
    ...dims.map((d): GridColDef => ({
      field: `__label_${d}`,
      headerName: dims.length > 1 ? labelForField(d) : 'Name',
      flex: 1.4,
      minWidth: 110,
      cellClassName: 'mx-name',
      valueGetter: (_v, row) => dimName(row, d),
    })),
    // Live-activity chip + click-through popover, for entity groupings the server returns it for.
    ...(showActivity ? [{
      field: '__activity',
      headerName: 'Activity',
      width: 118,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ActivityCell
          activity={params.row.activity as ActivityEntry | undefined}
          kind={activityKind(groupBy)}
          entityId={String(params.row.group_key)}
          updatedAt={params.row.activity_updated_at as string | null | undefined}
        />
      ),
    } as GridColDef] : []),
    // Flex (not fixed width) so a narrow panel shrinks the money columns to fit rather
    // than clipping the rightmost metrics behind a horizontal scroll.
    ...panel.query.metrics.map((m): GridColDef => ({
      field: m,
      headerName: metricLabel(m),
      flex: 1,
      minWidth: 68,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, row) => Number(row[m]),
      valueFormatter: (value) => formatMetric(m, value),
      cellClassName: (params) => `mx-${metricSign(m, params.value)}`,
    })),
  ];
  const rows = orderRowsByGroup(stats.rows, panel.query.group_by, panel.query.sort_order)
    .map((r, i) => ({ id: r.group_key || `row_${i}`, ...r }));

  return (
    <DataGridPremium
      rows={rows}
      columns={columns}
      density="compact"
      hideFooter
      disableRowSelectionOnClick
      disableColumnMenu
      columnHeaderHeight={38}
      onRowClick={linkable ? (params: GridRowParams) => openInTracking(params.row) : undefined}
      sx={{
        border: 0,
        height: '100%',
        fontVariantNumeric: 'tabular-nums',
        '& .MuiDataGrid-row': { cursor: linkable ? 'pointer' : 'default' },
        '--DataGrid-rowBorderColor': 'transparent',
        '& .MuiDataGrid-columnSeparator': { display: 'none' },
        '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': { '&:focus, &:focus-within': { outline: 'none' } },
        '& .MuiDataGrid-columnHeaders': { bgcolor: 'transparent' },
        '& .MuiDataGrid-columnHeader': { borderBottom: 1, borderColor: 'divider' },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontSize: '0.68rem', fontWeight: 600, letterSpacing: 0.4,
          textTransform: 'uppercase', color: 'text.secondary',
        },
        '& .MuiDataGrid-cell': { fontSize: '0.8125rem', borderBottom: 1, borderColor: 'divider' },
        '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': { borderBottom: 0 },
        '& .mx-name': { fontWeight: 600 },
        '& .mx-pos': { color: 'success.main', fontWeight: 600 },
        '& .mx-neg': { color: 'error.main', fontWeight: 600 },
      }}
    />
  );
}
